import {Directive, OnInit} from '@angular/core';
import {Observable} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, startWith} from 'rxjs/operators';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ApiService} from '../../services/api.service';
import {DynamicImageSet} from '../../model/dynamic-image-set';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {TestArea} from '../test-area';
import {ErrorHelper} from '../ErrorHelper';
import {NotifierService} from 'angular-notifier';
import {AppComponent} from '../../app.component';
import {User} from '../../model/user';

export enum TestDynamicType { LAYERS, OPTIONS }

@Directive()
export abstract class TestDynamicComponent implements OnInit {

  TestDynamicType = TestDynamicType;
  user = new User();
  selectedDeploymentSet: any;
  allDeploymentSets: any[];
  filteredOptions: Observable<string[]>;
  filteredVersions: Observable<any[]>;

  dynamicImageSetForm: FormGroup;
  deploymentSetForm: FormGroup;

  models = ['LHD', 'RHD'];
  resolutions = [];
  previewAngles: any[];
  versions = [];
  availableViews = new AvailableViews();
  width: any;
  height: any;

  testArea = TestArea;
  selectedClass: string;
  selectedTestArea = TestArea.PREPROD;

  showImagesAndTree: any;
  isFormExpanded: boolean;

  dynamicImageSet: DynamicImageSet;

  constructor(
    private apiService: ApiService,
    private formBuilder: FormBuilder,
    private notifierService: NotifierService,
    private app: AppComponent,
  ) {
    this.createAndLoadFormWithInitialValues();
  }

  abstract getTestDynamicType(): TestDynamicType;

  abstract getTitle(): string;

  abstract getSubmitLabel(): string;

  private createAndLoadFormWithInitialValues(): void {

    this.dynamicImageSet = new DynamicImageSet();
    this.dynamicImageSet.viewport = 'exterior';
    this.dynamicImageSet.model = this.models[0];
    this.dynamicImageSet.resolution = '';
    this.dynamicImageSet.previewAngles = [''];

    this.deploymentSetForm = this.formBuilder.group({
      deploymentSet: ''
    });

    this.dynamicImageSetForm = this.formBuilder.group({
      viewport: this.dynamicImageSet.viewport,
      model: this.dynamicImageSet.model,
      resolution: this.dynamicImageSet.resolution,
      previewAngles: [this.dynamicImageSet.previewAngles],
      version: [
        this.dynamicImageSet.version,
        this.getTestDynamicType() === TestDynamicType.OPTIONS ? Validators.required : Validators.nullValidator,
      ]
    });

    this.filteredOptions = this.deploymentSetForm.controls.deploymentSet.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        map(term => this.allDeploymentSets.filter(
          ds => ds.toLowerCase().indexOf(term.toLowerCase()) > -1
        ))
      );

    this.dynamicImageSetForm.controls.resolution.valueChanges
      .subscribe(value => {
        if (this.resolutions.length) {
          this.previewAngles = this.resolutions.find(e => e.resolutionName === value).angleNameList;
          this.width = this.resolutions.find(e => e.resolutionName === value).width;
          this.height = this.resolutions.find(e => e.resolutionName === value).height;
        }
      });
    this.dynamicImageSetForm.controls.viewport.valueChanges
      .subscribe(value => {
        this.dynamicImageSet.viewport = value;
        this.loadResolutions();
      });
  }

  ngOnInit(): void {
    this.app.getUserData().then(user => {
      this.user = user;
      if (user.hasEmbargoPermissions()) {
        this.selectedClass = 'embargo';
        this.selectedTestArea = TestArea.EMBARGO;
      } else {
        this.selectedClass = 'preprod';
        this.selectedTestArea = TestArea.PREPROD;
      }
      this.getAllDeploymentSets();
    });
  }

  selectTestArea(test: TestArea): void {
    this.selectedTestArea = test;
    this.selectedClass = this.selectedTestArea.toLowerCase().split(' ').find(e => true);
    this.getAllDeploymentSets();
    this.revert();
  }

  getAllDeploymentSets(): void {
    this.apiService.getAllDeploymentSetsForTest(this.selectedTestArea)
      .subscribe(allSets => this.allDeploymentSets = allSets);
  }

  async loadDataAsync(): Promise<any> {
    try {
      await this.loadResolutions();
      await this.loadAvailableViews();
      await this.loadVersions();
    } catch (error) {
      console.log(error);
    } finally {
        this.filteredVersions = this.dynamicImageSetForm.controls.version.valueChanges
          .pipe(
            startWith(''),
            map(filterValue => this.versions.filter(version => version.versionId.toLowerCase().includes(filterValue.toLowerCase())
              || version.versionLabel.toLowerCase().includes(filterValue.toLowerCase())))
          );
    }
  }

  clearVersion(): void {
    this.dynamicImageSetForm.controls.version.setValue('');
  }

  previousVersion(): void {
    const currentValue = this.dynamicImageSetForm.controls.version.value;
    let nextValue;
    if (Boolean(currentValue) && Array.isArray(this.versions) && this.versions.length) {
      const index = this.versions.findIndex(version => currentValue.toLowerCase().includes(version.versionId.toLowerCase())
                                                    && currentValue.toLowerCase().includes(version.versionLabel.toLowerCase()));
      if (index !== -1 && index !== 0) {
        nextValue = this.versions[index - 1];
      } else {
        nextValue = this.versions[this.versions.length - 1];
      }
      this.dynamicImageSetForm.controls.version.setValue(nextValue.versionId + ' - ' + nextValue.versionLabel);
    } else if (Array.isArray(this.versions) && this.versions.length) {
      nextValue = this.versions[this.versions.length - 1];
      this.dynamicImageSetForm.controls.version.setValue(nextValue.versionId + ' - ' + nextValue.versionLabel);
    }
  }

  nextVersion(): void {
    const currentValue = this.dynamicImageSetForm.controls.version.value;
    let nextValue;
    if (Boolean(currentValue) && Array.isArray(this.versions) && this.versions.length) {
      const index = this.versions.findIndex(version => currentValue.toLowerCase().includes(version.versionId.toLowerCase())
                                                    && currentValue.toLowerCase().includes(version.versionLabel.toLowerCase()));
      if (index !== -1 && index !== this.versions.length - 1) {
        nextValue = this.versions[index + 1];
      } else {
        nextValue = this.versions[0];
      }
      this.dynamicImageSetForm.controls.version.setValue(nextValue.versionId + ' - ' + nextValue.versionLabel);
    } else if (Array.isArray(this.versions) && this.versions.length) {
      nextValue = this.versions[0];
      this.dynamicImageSetForm.controls.version.setValue(nextValue.versionId + ' - ' + nextValue.versionLabel);
    }
  }

  loadVersions(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      return this.apiService.getVersions(this.selectedDeploymentSet, this.selectedTestArea).toPromise()
        .then(
          data => {
            this.versions = data;
            console.log('Fetched the versions');
            resolve(true);
          }
        )
        .catch(
          error => {
            const errorMessage = ErrorHelper.disectError('load versions', error);
            this.notifierService.notify('error', errorMessage);
            resolve(false);
          }
        );
    });
  }

  loadAvailableViews(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      return this.apiService.getAvailableViews(this.selectedDeploymentSet, this.selectedTestArea).toPromise()
        .then(
          data => {
            this.availableViews.isInteriorValid = data.isInteriorValid;
            this.availableViews.isExteriorValid = data.isExteriorValid;
            if (this.dynamicImageSetForm.disabled) {
              this.dynamicImageSetForm.enable();
            }
            if (this.availableViews.isExteriorValid) {
              this.dynamicImageSetForm.controls.viewport.setValue('exterior');
            } else if (this.availableViews.isInteriorValid) {
              this.dynamicImageSetForm.controls.viewport.setValue('interior');
            } else {
              this.dynamicImageSetForm.controls.viewport.disable();
              this.dynamicImageSetForm.disable();
              this.notifierService.notify('error', 'Insufficient data was uploaded for exterior / interior view for ' +
                this.selectedDeploymentSet);
            }
            console.log('Fetched the available views');
            resolve(true);
          })
        .catch(error => {
          this.availableViews.isInteriorValid = true;
          this.availableViews.isExteriorValid = true;
          const errorMessage = ErrorHelper.disectError('load available views', error);
          this.notifierService.notify('error', errorMessage);
          resolve(false);
        });
    });
  }

  loadResolutions(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (this.dynamicImageSet.id !== null && this.dynamicImageSet.id !== '') {
        return this.apiService.getResolutions(this.dynamicImageSet, this.selectedTestArea).toPromise()
          .then(data => {
            this.resolutions = data;
            this.dynamicImageSetForm.controls.resolution.patchValue(this.resolutions[0].resolutionName);
            this.dynamicImageSetForm.controls.previewAngles.patchValue(this.resolutions[0].angleNameList);
            console.log('Fetched the resolutions');
            resolve(true);
          })
          .catch(error => {
            const errorMessage = ErrorHelper.disectError('load resolutions', error);
            this.notifierService.notify('error', errorMessage);
            resolve(false);
          });
      } else {
        this.resolutions = [];
        resolve(true);
      }
    });
  }

  selectedItem(event: MatAutocompleteSelectedEvent): void {
    console.log(event.option.value);
    this.selectedDeploymentSet = event.option.value;
    this.dynamicImageSet.id = this.selectedDeploymentSet;
    this.loadDataAsync();
    this.isFormExpanded = true;
  }

  submitDis(): void {
    const result: any = Object.assign({}, this.dynamicImageSetForm.value);
    this.dynamicImageSet = new DynamicImageSet(result);
    this.dynamicImageSet.id = this.selectedDeploymentSet;
    this.dynamicImageSet.width = this.width;
    this.dynamicImageSet.height = this.height;
    this.dynamicImageSet.version = this.dynamicImageSet.version.slice(0, 16);
    this.showImagesAndTree = true;
    this.deploymentSetForm.disable();
  }

  revert(): void {
    this.showImagesAndTree = false;
    this.isFormExpanded = false;
    this.selectedDeploymentSet = null;
    this.deploymentSetForm.enable();
    this.dynamicImageSetForm.enable();
    this.createAndLoadFormWithInitialValues();
  }

}

export class AvailableViews {
  isInteriorValid: boolean;
  isExteriorValid: boolean;
}
